
import { Stroke, Style, Fill } from 'ol/style';
import { DragBox, Select, Snap } from 'ol/interaction';
import { Map as MGMap } from '@meteogroup/map-layers';
import { click, shiftKeyOnly, singleClick } from 'ol/events/condition'
import { VoyageLayersNames } from './types/voyage-layers.types';
import CircleStyle from 'ol/style/Circle';
import BaseLayer from 'ol/layer/Base';
import { Layer } from 'ol/layer';
import { Feature, MapBrowserEvent } from 'ol';
import RenderFeature from 'ol/render/Feature';
import { Geometry } from 'ol/geom';
import { VoyWeatherBroadcastManager } from '../../local_modules/voy-weather-broadcast-drawer/src/voy-weather-brodcast-manager';
import { UiStateModel } from '../models/ui-state-model';

export class WeatherBroadcastingLayer extends EventTarget {
    constructor() {
        super();

        this._onDragEnd = this._onDragEnd.bind(this);
        this._onMapClick = this._onMapClick.bind(this);
    }

    private _map: MGMap|null = null;
    private _layers: Layer<any>[] = [];
    
    enterVesselSelectionMode(map: MGMap) {
       //disable the focusing of left side on enabling the list view, so user can drag from the left end
        UiStateModel?.setFocusPanel(false)
        this._map = map;

        this._map!.addInteraction(this._dragBox);
        
        if (!this._layers.length) {
            this._assignLayers();
        }

        this._dragBox.on('boxend', this._onDragEnd);
        this._map.on('click', this._onMapClick);
    }

    exitVesselSelectionMode(imos: number[]) {
        UiStateModel?.setFocusPanel(true)
        this._map!.removeInteraction(this._dragBox);
        this._dragBox.un('boxend', this._onDragEnd);

        for (const layer of this._layers) {
            for (const imo of imos) {
                layer.getSource().getFeatureById(imo).setProperties({
                    isSelected: false
                })
            }
        }

        this._map!.un('click', this._onMapClick);
    }

    private _onMapClick(e: MapBrowserEvent<any>) {
        if (!e.originalEvent.shiftKey) {
            return
        }
        this._map!.forEachFeatureAtPixel(e.pixel, (f: RenderFeature | Feature<Geometry>) => {
            const isVesselsLayer = f.getProperties().layer === VoyageLayersNames.VesselsLayer;
            if (!isVesselsLayer) return;

            const clickedFeature = f as Feature<Geometry>;
            
            const selected: number[] = [];
            const deselected: number[] = [];
            this._setIsSelectedProperty(clickedFeature, selected, deselected);
            VoyWeatherBroadcastManager!.setSelection(deselected, selected);

            return true;
        });
    }

    private _dragBox = new DragBox({
        // The css styling is set in voyage-ol-map.ts
        // Based on https://stackoverflow.com/questions/56738857/openlayers-cannot-style-dragbox
        className: "drag-box"
    });
    
    private _onDragEnd() {
        const extend = this._dragBox.getGeometry().getExtent();

        let boxFeatures: Feature<Geometry>[] = [];
        for (const layer of this._layers) {
            const layerBoxFeatures = layer.getSource()
            .getFeaturesInExtent(extend,this._map?.getView().getProjection());
            boxFeatures = boxFeatures.concat(layerBoxFeatures);
        }
      
        const selected: number[] = [];
        const deselected: number[] = [];
        for (const feature of boxFeatures) {
            this._setIsSelectedProperty(feature, selected, deselected);
        }
        VoyWeatherBroadcastManager!.setSelection(deselected, selected);
    }

    private _setIsSelectedProperty(clickedFeature: Feature<Geometry>, selected: number[], deselected: number[]) {

        if (clickedFeature.get("isSelected")) {
            deselected.push(clickedFeature.get("imoNo"));
            clickedFeature.set("isSelected", false);
            clickedFeature.changed(); //TODO is it necessary to call this changed?
        } else {
            selected.push(clickedFeature.get("imoNo"));
            clickedFeature.set("isSelected", true);
            clickedFeature.changed(); //TODO is it necessary to call this changed?
        }
    }

    private _assignLayers() {
        const baseLayers = this._map!.getLayers().getArray().filter(layer => {
            const layerName = layer.get('layerName');
            return layerName === VoyageLayersNames.VectorImageVesselsLayer || layerName === VoyageLayersNames.WebGlVesselsLayer;
        })
        
        if (!baseLayers) return;

        this._layers = baseLayers.flatMap(baseLayer => baseLayer.getLayersArray());
    }
}
